import {
  type SimpleShowResPagedData,
  sitesV1NationalShowsList,
  sitesV1SitesShowsDetail,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { AdSidebarLayout } from 'components/Layouts/AdSidebarLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { TeamShowsIndex } from 'components/Shows/TeamShowsIndex';
import type { GetServerSideProps, GetServerSidePropsResult } from 'next';

export interface IShowsIndexProps extends IGenericPageProps {
  siteList?: SimpleShowResPagedData | null;
  nationalList?: SimpleShowResPagedData | null;
  title?: string;
}

const ShowsListPage = ({
  siteData,
  siteList,
  nationalList,
  ...props
}: IShowsIndexProps) => {
  const siteName = siteData?.currentSite?.siteName;
  const organization = siteData?.currentSite?.organization;
  const orgName = organization?.knownAs ?? organization?.name;

  const description = `Explore the Latest ${siteName} Podcast & Shows. Get the latest insights and updates from hosts covering the ${orgName} ${organization?.mascot}.`;
  const title = `${siteName} Podcast & Shows`;

  const noIndex = siteList?.pagination?.count === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      siteData={siteData}
      title={title}
    >
      <AdSidebarLayout>
        <TeamShowsIndex
          nationalList={nationalList}
          siteData={siteData}
          siteList={siteList}
          title={title}
        />
      </AdSidebarLayout>
    </PageWrapper>
  );
};

// Example of typing serversideprops to match component
export const getServerSideProps: GetServerSideProps<IShowsIndexProps> = async (
  ctx,
): Promise<GetServerSidePropsResult<IShowsIndexProps>> => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return { notFound: true };
  }

  try {
    const [siteList, nationalList] = await Promise.allSettled([
      sitesV1SitesShowsDetail(proxyApi, siteData.currentSite?.key || 0),
      sitesV1NationalShowsList(proxyApi, {}),
    ]);

    return {
      props: {
        siteData,
        userData,
        siteList: getFulfilledValue(siteList),
        nationalList: getFulfilledValue(nationalList),
      },
    };
  } catch (e) {
    return { props: { pageType: 'shows', siteData } };
  }
};

export default withValidUrl(ShowsListPage);
